import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Button,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  Chip,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  InputAdornment,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { PersonAdd, UploadFile, ArrowBack, Download, Search as SearchIcon } from "@mui/icons-material";
import QRCode from "react-qr-code";
import specialSessionService from "../../services/specialSessionService";
import chatService from "../../services/chatService";
import { formatLectureScheduleStatus } from "../../utils/lectureScheduleStatus";
import { formatDateTimeIST } from "../../utils/timetableTimeSlots";

const isValidEmail = (value) => {
  const v = String(value || "").trim();
  if (!v) return true; // optional
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
};

const isValidPhone = (value) => {
  const v = String(value || "").trim();
  if (!v) return true; // optional
  const digits = v.replace(/[^\d]/g, "");
  return digits.length >= 7 && digits.length <= 15;
};

const SpecialSessionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const sessionId = Number(id);

  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [tab, setTab] = useState("internal");
  const [rows, setRows] = useState([]);
  const [attendeeCount, setAttendeeCount] = useState({ marked: 0, total: 0 });
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [addOpen, setAddOpen] = useState(false);
  const [externalForm, setExternalForm] = useState({
    attendeeName: "",
    attendeeEmail: "",
    attendeePhone: "",
    attendeeCompany: "",
    attendeeDesignation: "",
    attendeeNotes: "",
  });
  const [externalErrors, setExternalErrors] = useState({ attendeeEmail: "", attendeePhone: "" });

  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);

  // Internal student marking state
  const [markInternalOpen, setMarkInternalOpen] = useState(false);
  const [internalSearch, setInternalSearch] = useState("");
  const [internalSearchResults, setInternalSearchResults] = useState([]);
  const [internalSearchLoading, setInternalSearchLoading] = useState(false);
  const [markingUserId, setMarkingUserId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [sRes, aRes, cRes] = await Promise.all([
        specialSessionService.getById(sessionId),
        specialSessionService.listAttendees(sessionId, { type: tab }),
        specialSessionService.getAttendeeCount(sessionId),
      ]);
      setSession(sRes?.data?.data ?? sRes?.data);
      setRows(aRes?.data?.data ?? aRes?.data ?? []);
      const countData = cRes?.data?.data ?? {};
      setAttendeeCount({ marked: countData.marked || 0, total: countData.total || 0 });
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!Number.isFinite(sessionId)) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, tab]);

  // Auto-refresh session data every 5s when live (for QR/OTP/count updates)
  useEffect(() => {
    if (!session || session.status !== "live") return;
    const interval = setInterval(() => {
      specialSessionService.getById(sessionId).then((res) => {
        const s = res?.data?.data ?? res?.data;
        if (s) setSession(s);
      }).catch(() => {});
      specialSessionService.getAttendeeCount(sessionId).then((res) => {
        const d = res?.data?.data ?? {};
        setAttendeeCount({ marked: d.marked || 0, total: d.total || 0 });
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.status, sessionId]);

  // Search internal users for marking attendance
  const handleInternalSearch = async (query) => {
    setInternalSearch(query);
    if (query.trim().length < 2) {
      setInternalSearchResults([]);
      return;
    }
    setInternalSearchLoading(true);
    try {
      const res = await chatService.getChatUsers({ search: query.trim(), limit: 20 });
      const users = res?.data?.data || [];
      setInternalSearchResults(users.filter((u) => u.role === "student" || u.role === "teacher" || u.role === "hod"));
    } catch {
      setInternalSearchResults([]);
    } finally {
      setInternalSearchLoading(false);
    }
  };

  const handleMarkInternal = async (user) => {
    if (!user?.id) return;
    setMarkingUserId(user.id);
    try {
      await specialSessionService.manualMark(sessionId, { userId: user.id, attendeeType: user.role || "student" });
      setSnackbar({ open: true, severity: "success", message: `Marked ${user.fullName || user.firstName || "user"} as present` });
      load();
    } catch (e) {
      setSnackbar({ open: true, severity: "error", message: e?.response?.data?.message || "Failed to mark" });
    } finally {
      setMarkingUserId(null);
    }
  };

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 90 },
      {
        field: "attendeeDisplayName",
        headerName: "Name",
        flex: 1,
        minWidth: 180,
        valueGetter: (p) =>
          p?.row?.attendeeDisplayName ||
          p?.row?.attendeeName ||
          p?.row?.user?.fullName ||
          [p?.row?.user?.firstName, p?.row?.user?.lastName].filter(Boolean).join(" ") ||
          "—",
      },
      {
        field: "attendeeType",
        headerName: "Type",
        width: 120,
        valueGetter: (p) => {
          const t = String(p?.row?.attendeeType || p?.row?.user?.role || "").toLowerCase();
          if (t === "hod") return "HOD";
          if (t === "student") return "Student";
          if (t === "teacher") return "Teacher";
          if (t === "staff") return "Staff";
          if (t === "outsider") return "External";
          return t ? t.charAt(0).toUpperCase() + t.slice(1) : "—";
        },
      },
      {
        field: "methodDisplay",
        headerName: "Method",
        width: 150,
        valueGetter: (p) => p?.row?.methodDisplay || p?.row?.markedVia || "—",
      },
      {
        field: "markedAt",
        headerName: "Marked At",
        width: 190,
        valueGetter: (p) => formatDateTimeIST(p?.row?.markedAt) || "—",
      },
      {
        field: "attendanceStartedByName",
        headerName: "Attendance Started By",
        width: 200,
        valueGetter: (p) => p?.row?.attendanceStartedByName || "—",
      },
      {
        field: "checkInByName",
        headerName: "Check-in By",
        width: 180,
        valueGetter: (p) => p?.row?.checkInByName || "—",
      },
    ],
    []
  );

  const onPreviewImport = async () => {
    if (!importFile) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const res = await specialSessionService.importExternalAttendees(sessionId, fd, { preview: "true" });
      setImportPreview(res?.data?.data ?? null);
    } catch (e) {
      setSnackbar({ open: true, severity: "error", message: e?.response?.data?.message || "Preview failed" });
    } finally {
      setLoading(false);
    }
  };

  const onImport = async () => {
    if (!importFile) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      await specialSessionService.importExternalAttendees(sessionId, fd, { preview: "false" });
      setSnackbar({ open: true, severity: "success", message: "Imported external participants" });
      setImportOpen(false);
      setImportPreview(null);
      setImportFile(null);
      load();
    } catch (e) {
      setSnackbar({ open: true, severity: "error", message: e?.response?.data?.message || "Import failed" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          {/* Back button + Header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <IconButton onClick={() => navigate(-1)} size="small">
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Session Details
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 2, flexWrap: "wrap" }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {session?.title ? `${session.title} (ID: ${session.id})` : `Session ID: ${sessionId}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Status:{" "}
                {session?.myAttendanceMarked
                  ? "Completed"
                  : formatLectureScheduleStatus(session?.status)}{" "}
                | QR: {session?.qrEnabled ? "On" : "Off"} | OTP:{" "}
                {session?.otpEnabled ? "On" : "Off"} | Manual:{" "}
                {session?.manualEnabled ? "On" : "Off"}
              </Typography>

              {/* Count summary */}
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <Chip
                  label={`Present: ${attendeeCount.marked}`}
                  color="success"
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Total: ${attendeeCount.total}`}
                  color="primary"
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Absent: ${Math.max(0, attendeeCount.total - attendeeCount.marked)}`}
                  color="error"
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                color="success"
                startIcon={<Download />}
                onClick={async () => {
                  try {
                    const res = await specialSessionService.exportAttendees(sessionId);
                    const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${(session?.title || "session").replace(/[^a-zA-Z0-9]/g, "_")}_attendees.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    setSnackbar({ open: true, severity: "error", message: e?.response?.data?.message || "Failed to download" });
                  }
                }}
              >
                Download Excel
              </Button>
              <Button
                variant="contained"
                startIcon={<PersonAdd />}
                disabled={!session?.manualEnabled}
                onClick={() => setMarkInternalOpen(true)}
              >
                Mark Internal Student
              </Button>
              <Button
                variant="outlined"
                startIcon={<PersonAdd />}
                disabled={!session?.allowExternalParticipants || !session?.manualEnabled}
                onClick={() => setAddOpen(true)}
              >
                Add External
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadFile />}
                disabled={!session?.allowExternalParticipants}
                onClick={() => setImportOpen(true)}
              >
                Import External
              </Button>
            </Box>
          </Box>

          {/* Live QR/OTP display for projector — only when session is live */}
          {session?.status === "live" && (session?.qrPublicId || session?.otpCode) ? (
            <Box sx={{ mt: 2, p: 2, border: "2px solid", borderColor: "success.main", borderRadius: 2, display: "flex", gap: 4, justifyContent: "center", alignItems: "center", flexWrap: "wrap", bgcolor: "grey.50" }}>
              {session.qrPublicId ? (
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: "success.main" }}>
                    QR Code (Live)
                  </Typography>
                  <Box sx={{ display: "inline-block", p: 2, bgcolor: "white", borderRadius: 2, boxShadow: 2 }}>
                    <QRCode value={String(session.qrPublicId)} size={180} />
                  </Box>
                </Box>
              ) : null}
              {session.otpCode ? (
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: "success.main" }}>
                    OTP Code (Live)
                  </Typography>
                  <Box sx={{ display: "inline-block", p: 2, bgcolor: "white", borderRadius: 2, boxShadow: 2 }}>
                    <Typography variant="h3" sx={{ fontFamily: "monospace", fontWeight: 800, letterSpacing: 4 }}>
                      {session.otpCode}
                    </Typography>
                  </Box>
                  {session.otpExpiresAt ? (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }} color="text.secondary">
                      Expires: {new Date(session.otpExpiresAt).toLocaleTimeString()}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}
            </Box>
          ) : null}

          <Tabs sx={{ mt: 2 }} value={tab} onChange={(_, v) => setTab(v)}>
            <Tab value="internal" label="Internal Users" />
            <Tab value="external" label="External Participants" />
          </Tabs>

          {error ? (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : null}

          <Box sx={{ mt: 2, height: 560 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              disableSelectionOnClick
              getRowId={(r) => r.id}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add External Participant</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name *"
                value={externalForm.attendeeName}
                onChange={(e) => setExternalForm((p) => ({ ...p, attendeeName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                value={externalForm.attendeeEmail}
                error={!!externalErrors.attendeeEmail}
                helperText={externalErrors.attendeeEmail || "Optional"}
                onChange={(e) => {
                  const v = e.target.value;
                  setExternalForm((p) => ({ ...p, attendeeEmail: v }));
                  setExternalErrors((p) => ({ ...p, attendeeEmail: isValidEmail(v) ? "" : "Invalid email" }));
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={externalForm.attendeePhone}
                error={!!externalErrors.attendeePhone}
                helperText={externalErrors.attendeePhone || "Optional (7–15 digits)"}
                onChange={(e) => {
                  const v = e.target.value;
                  setExternalForm((p) => ({ ...p, attendeePhone: v }));
                  setExternalErrors((p) => ({ ...p, attendeePhone: isValidPhone(v) ? "" : "Invalid phone number" }));
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Organization / Company" value={externalForm.attendeeCompany} onChange={(e) => setExternalForm((p) => ({ ...p, attendeeCompany: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Designation" value={externalForm.attendeeDesignation} onChange={(e) => setExternalForm((p) => ({ ...p, attendeeDesignation: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Notes" value={externalForm.attendeeNotes} onChange={(e) => setExternalForm((p) => ({ ...p, attendeeNotes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              loading ||
              !externalForm.attendeeName ||
              !isValidEmail(externalForm.attendeeEmail) ||
              !isValidPhone(externalForm.attendeePhone)
            }
            onClick={async () => {
              setLoading(true);
              try {
                await specialSessionService.manualMark(sessionId, { ...externalForm, attendeeType: "outsider" });
                setSnackbar({ open: true, severity: "success", message: "Marked present" });
                setAddOpen(false);
                setExternalForm({ attendeeName: "", attendeeEmail: "", attendeePhone: "", attendeeCompany: "", attendeeDesignation: "", attendeeNotes: "" });
                setExternalErrors({ attendeeEmail: "", attendeePhone: "" });
                load();
              } catch (e) {
                setSnackbar({ open: true, severity: "error", message: e?.response?.data?.message || "Failed" });
              } finally {
                setLoading(false);
              }
            }}
          >
            {loading ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import External Participants</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              File requirements
            </Typography>
            <Typography variant="body2">
              - Supported: <b>CSV</b>, <b>XLSX</b>
            </Typography>
            <Typography variant="body2">
              - Required column: <b>Name</b>
            </Typography>
            <Typography variant="body2">
              - Optional columns: Phone Number, Email, Organization, Designation, Notes
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Tip: Use <b>Preview</b> first to see row-level errors. Duplicate rows in the same file are rejected.
            </Typography>
          </Alert>

          <Box sx={{ mt: 2, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={async () => {
                try {
                  const r = await specialSessionService.downloadExternalTemplate();
                  const csv = r?.data?.csvData || "";
                  const fileName = r?.data?.fileName || "external_participants_template.csv";
                  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = fileName;
                  a.click();
                  window.URL.revokeObjectURL(url);
                } catch (e) {
                  setSnackbar({ open: true, severity: "error", message: e?.response?.data?.message || "Template download failed" });
                }
              }}
            >
              Download Template
            </Button>
            <Button variant="outlined" component="label">
              Choose File
              <input
                type="file"
                hidden
                accept=".csv,.xlsx,.xls"
                onChange={(e) => {
                  setImportFile(e.target.files?.[0] || null);
                  setImportPreview(null);
                }}
              />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {importFile ? importFile.name : "No file selected"}
            </Typography>
          </Box>

          {importPreview ? (
            <Box sx={{ mt: 2 }}>
              <Alert severity="success">
                Preview: success {importPreview?.summary?.successCount ?? 0} / failed {importPreview?.summary?.failedCount ?? 0}
              </Alert>
              {Array.isArray(importPreview?.failedRows) && importPreview.failedRows.length ? (
                <Box sx={{ mt: 1 }}>
                  <Alert severity="warning">Some rows failed. First error: {importPreview.failedRows[0]?.reason}</Alert>
                </Box>
              ) : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)} disabled={loading}>
            Close
          </Button>
          <Button onClick={onPreviewImport} variant="outlined" disabled={loading || !importFile}>
            {loading ? <CircularProgress size={18} /> : "Preview"}
          </Button>
          <Button onClick={onImport} variant="contained" disabled={loading || !importFile}>
            {loading ? <CircularProgress size={18} /> : "Import"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark Internal Student Dialog */}
      <Dialog open={markInternalOpen} onClose={() => { setMarkInternalOpen(false); setInternalSearch(""); setInternalSearchResults([]); }} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Internal Student / Staff</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or email (min 2 chars)..."
            value={internalSearch}
            onChange={(e) => handleInternalSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          {internalSearchLoading ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : internalSearchResults.length > 0 ? (
            <List dense sx={{ maxHeight: 300, overflow: "auto" }}>
              {internalSearchResults.map((user) => (
                <ListItemButton
                  key={user.id}
                  onClick={() => handleMarkInternal(user)}
                  disabled={markingUserId === user.id}
                >
                  <ListItemText
                    primary={user.fullName || `${user.firstName || ""} ${user.lastName || ""}`.trim() || "User"}
                    secondary={`${user.role || "student"} • ${user.email || ""}`}
                  />
                  {markingUserId === user.id ? <CircularProgress size={18} /> : null}
                </ListItemButton>
              ))}
            </List>
          ) : internalSearch.trim().length >= 2 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
              No users found
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
              Type at least 2 characters to search
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMarkInternalOpen(false); setInternalSearch(""); setInternalSearchResults([]); }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SpecialSessionDetails;

